import {
  Action,
  combineReducers,
  configureStore,
  ThunkAction,
} from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  createMigrate,
  FLUSH,
  MigrationManifest,
  PAUSE,
  PERSIST,
  PersistedState,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage';
import apiExplorerReducer from '../features/apiExplorer/apiExplorerSlice';
import authReducer from '../features/auth/authSlice';
import dataGridTablesReducer from '../features/dataGridTables/dataGridTablesSlice';
import dataMeshReducer from '../features/dataMesh/dataMeshSlice';
import debugReducer from '../features/debugInfo/debugSlice';
import duckDBReducer from '../features/duckDB/duckDBSlice';
import imageReducer from '../features/imageViewer/imageSlice';
import mapReducer from '../features/map/mapSlice';
import onboardingReducer from '../features/onboarding/onboardingSlice';
import ranchReducer from '../features/ranches/ranchesSlice';
import scanDataReducer from '../features/scanDataApi/scanDataSlice';
import scansReducer from '../features/scans/scansSlice';
import selectReducer from '../features/select/selectSlice';
import cubeReducer from '../services/data/impl/cube/cubeSlice';
import { wranglerSlice } from '../services/data/impl/wrangler/wranglerSlice';
import { apiSlice } from '../services/entities/impl/blfApi/apiSlice';
import { featureFlagsSlice } from '../services/featureFlags/featureFlagSlice';

const rootReducer = combineReducers({
  scans: scansReducer,
  select: selectReducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
  map: mapReducer,
  ranch: ranchReducer,
  cube: cubeReducer,
  onboarding: onboardingReducer,
  image: imageReducer,
  auth: authReducer,
  scanData: scanDataReducer,
  duckDB: duckDBReducer,
  dataMesh: dataMeshReducer,
  dataGridTables: dataGridTablesReducer,
  [wranglerSlice.reducerPath]: wranglerSlice.reducer,
  apiExplorer: apiExplorerReducer,
  debug: debugReducer,
  [featureFlagsSlice.name]: featureFlagsSlice.reducer,
});

const migrations = {
  1: (
    state: PersistedState & {
      flags: object;
    }
  ) => ({
    ...state,
    flags: {
      ...state.flags,
      featureFlags: {
        // @ts-expect-error This is a migration, so we can ignore type errors
        ...state.flags.featureFlags,
        BLUEBERRY_COLOR_2: false,
      },
    },
  }),
  2: (
    state: PersistedState & { flags: { qaMode: boolean; devMode: boolean } }
  ) => ({
    viewMode: {
      qaMode: state.flags.qaMode,
      devMode: state.flags.devMode,
    }, // remove flags from persisted state
  }),
  3: () => ({}), // Remove viewMode from persisted state
} as unknown as MigrationManifest;

const persistConfig = {
  key: 'root',
  storage,
  stateReconciler: autoMergeLevel2,
  whitelist: [], // List of slice names to persist
  blacklist: [apiSlice.reducerPath, wranglerSlice.reducerPath],
  version: 3, // Increment this when you change state shape
  migrate: createMigrate(migrations, { debug: false }),
};
const persistedReducer = persistReducer<ReturnType<typeof rootReducer>>(
  persistConfig,
  rootReducer
);

// Out of sentry trace size limit
// const sentryReduxEnhancer = Sentry.createReduxEnhancer({
//   // Optionally pass options listed below
// });

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: { warnAfter: 500 },
      serializableCheck: {
        warnAfter: 500,
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(apiSlice.middleware, wranglerSlice.middleware),
  enhancers: [
    // sentryReduxEnhancer,
  ],
});

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
